import { useState } from "react";
import Datepicker from "react-tailwindcss-datepicker";
import { Card, CategoryBar, Divider } from "@tremor/react";
import { useNavigate } from "react-router-dom";
import {
  useAssessmentMetrics,
  useGetAllVQAList,
  useGetAllVQAListAdminRequest,
  useGetMyRfis,
  useGetSponsorCount,
  useGetVendorRfis,
  useProviderAssessmentMetrics,
  useProviderRFIMetrics,
  useProviderTotalCount,
  useSponsorRFIMetrics,
  useTotalAssessment,
  useTotalRFIs,
  useTotalSponsorCount,
} from "../../../custom-hooks/queries";
import StatusIndicator from "../../../components/StatusIndicator";
import { formatVqqName, formatVqqRowNameWithLocation } from "../../../utils/vqqNameFormat";
import SummaryInfoCards from "../../../components/StatusDashboard/SummaryInfoCards";
import RFIVQAStatusTabs from "../../StatusDashboard/RFIVQAStatusTabs";
import SkeletonLoader from "../../../components/SkeletonLoader";
import CustomTable from "../../../components/CustomTable";
import { dateFormat, useUserRole } from "../../../utils/helperMethods";
import Capabilities from "../../../components/Capabilities";
import { dummyRfiResponses } from "../../../utils/constants";
import { useSelector } from "react-redux";
import { useDecodedToken } from "../../../custom-hooks/useToken";

export default function ProviderStatusDashboard() {
  const [activeTab, setActiveTab] = useState("RFI");
  const [activeProviderSummaryTab, setActiveProviderSummaryTab] =
    useState("Top Providers");

  const user = useUserRole();

  const [activeProviderCtg, setActiveProviderCtg] = useState(null);
  const navigate = useNavigate();

  const {
    data: providerTotalCountData,
    isLoading: providerTotalCountIsLoading,
    isError: providerTotalCountIsError,
    error: providerTotalCountError,
    refetch: providerTotalCountRefetch,
  } = useProviderTotalCount();

  const {
    data: sponsorTotalCountData,
    isLoading: sponsorTotalCountIsLoading,
    isError: sponsorTotalCountIsError,
    error: sponsorTotalCountError,
    refetch: sponsorTotalCountRefetch,
  } = useTotalSponsorCount();

  const { decoded } = useDecodedToken();

  const { data: sponsorDistinctCount } = useGetSponsorCount(
    decoded?.provider_id
  );

  const {
    data: totalAssessmentData,
    isLoading: totalAssessmentIsLoading,
    isError: totalAssessmentIsError,
    error: totalAssessmentError,
    refetch: totalAssessmentRefetch,
  } = useTotalAssessment();

  const {
    data: assessmentMetricsData,
    isLoading: assessmentMetricsIsLoading,
    isError: assessmentMetricsIsError,
    error: assessmentMetricsError,
    refetch: assessmentMetricsRefetch,
  } = useAssessmentMetrics();

  const {
    data: totalRfis,
    isLoading: totalRfisIsLoading,
    isError: totalRfisIsError,
    error: totalRfisError,
    refetch: totalRfisRefetch,
  } = useTotalRFIs();

  const {
    data: providerRfiMetricsData,
    isLoading: providerRfiMetricsIsLoading,
    isError: providerRfiMetricsIsError,
    error: providerRfiMetricsError,
    refetch: providerRfiMetricsRefetch,
  } = useProviderRFIMetrics();

  const {
    data: providerAssessmentMetrics,
    isLoading: providerAssessmentMetricsIsLoading,
    isError: providerAssessmentMetricsIsError,
    error: providerAssessmentMetricsError,
    refetch: providerAssessmentMetricsRefetch,
  } = useProviderAssessmentMetrics();

  const [currentPage, setCurrentPage] = useState(1);
  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllVQAList(21, [], [], [], []);

  const {
    data: rfisData,
    isLoading: rfiIsLoading,
    isError: rfiIsError,
    error: rfiError,
    refetch: rfiRefetch,
  } = useGetVendorRfis(currentPage);

  const [selectedIds, setSelectedIds] = useState({
    auditor: [],
    provider: [],
    status: [],
    services: [],
  });

  const {
    data: vqaData,
    isLoading: vqaIsLoading,
    isError: vqaIsError,
    error: vqaError,
  } = useGetAllVQAList(currentPage);

  const [sortConfig, setSortConfig] = useState({});
  const sortStatuses = (aStatus, bStatus, direction) => {
    const statusOrder = [
      "Pending Admin Review",
      "Accept Request",
      "Auditor Assigned",
      "Assessment Scheduled",
      "Assessment Conducted",
      "Sponsor Draft Report Available",
      "Provider Draft Report Available",
      "CAPA Response Available",
      "VQA",
      "Report Published to Library",
      "Denied",
    ];
    const aIndex = statusOrder.indexOf(aStatus);
    const bIndex = statusOrder.indexOf(bStatus);

    if (aIndex !== bIndex) {
      return direction === "asc" ? aIndex - bIndex : bIndex - aIndex;
    }
    return 0;
  };

  // Get the active data based on which tab is selected
  const activeData = activeTab === "RFI" ? rfisData?.items : vqaData?.items;

  const sortedItems = activeData?.sort((a, b) => {
    for (let key in sortConfig) {
      const { direction } = sortConfig[key];
      const valueA = a[key];
      const valueB = b[key];

      // Handle empty values: move them to the end in ascending and to the start in descending order
      const isEmptyA = valueA === null || valueA === undefined || valueA === "";
      const isEmptyB = valueB === null || valueB === undefined || valueB === "";

      if (isEmptyA && !isEmptyB) return direction === "asc" ? 1 : -1;
      if (!isEmptyA && isEmptyB) return direction === "asc" ? -1 : 1;
      if (isEmptyA && isEmptyB) return 0; // Both are empty, keep order unchanged

      if (key === "status") {
        return sortStatuses(valueA, valueB, direction);
      } else if (valueA !== valueB) {
        return direction === "asc"
          ? valueA > valueB
            ? 1
            : -1
          : valueA < valueB
          ? 1
          : -1;
      }
    }
    return 0;
  });

  // Handle sorting for both RFI and VQA lists
  const handleSort = (key) => {
    setSortConfig({
      [key]: {
        direction: sortConfig[key]?.direction === "asc" ? "desc" : "asc",
      },
    });
  };

  const RfiColumns = [
    ...(user === "Backend User" || user === "Backend Master User"
      ? [
          {
            key: "provider",
            header: "Vendor",
            sortable: false,
            width: "16%",
            render: (item) => (
              <div
                className="hover:text-gray-700 hover:cursor-pointer hover:underline"
                onClick={() =>
                  navigate(`/RFI/${item?.questionnaire_id}/Details`)
                }
              >
                <div className="font-semibold text-black">
                  {formatVqqRowNameWithLocation(item)}
                  <div className="text-xs font-thin">
                    {item?.name && `${formatVqqName(item?.name)} - ${item?.services}`}
                  </div>
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      key: "sponsor",
      header: "Sponsor",
      sortable: false,
      width: "12%",
      render: (item) => (
        <div
          className="hover:text-gray-700 hover:cursor-pointer hover:underline"
          onClick={() => navigate(`/RFI/${item?.questionnaire_id}/Details`)}
        >
          <div className="font-semibold text-black">{item?.sponsorName}</div>
        </div>
      ),
    },
    {
      key: "template",
      header: "Name",
      sortable: false,
      width: "20%",
      render: (item) => (
        <div>
          <div className="font-thin">{formatVqqRowNameWithLocation(item)}</div>
        </div>
      ),
    },
    {
      key: "services",
      header: "Template",
      sortable: false,
      width: "12%",
      render: (item) => (
        <div className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-green-600/20 ring-inset">
          {item?.services}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: false,
      width: "12%",
      render: (item) => <StatusIndicator status={item?.status} />,
    },
    {
      key: "RequestDate",
      header: "Request Date",
      sortable: false,
      width: "14%",
      render: (item) => dateFormat(item?.requestDate),
    },
    {
      key: "initialReleaseDate",
      header: "Release Date",
      sortable: false,
      width: "14%",
      render: (item) => {
        const v = item?.initialReleaseDate ?? item?.initial_release_date;
        return v ? dateFormat(v) : "--";
      },
    },
    {
      key: "releaseDate",
      header: "Completion Date",
      sortable: false,
      width: "14%",
      render: (item) =>
        item?.releaseDate ? dateFormat(item?.releaseDate) : "--",
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      width: "auto",
      render: (item) => (
        <span
          className="font-semibold cursor-pointer text-black hover:underline"
          onClick={() => navigate(`/RFI/${item?.questionnaire_id}/Details`)}
        >
          View
        </span>
      ),
    },
  ];

  const VQAColumns = [
    {
      key: "sponsor_name",
      header: "Sponsor",
      sortable: false,
      width: "16%",
      render: (item) => (
        <div
        // className="hover:text-gray-700 hover:cursor-pointer hover:underline"
        // onClick={() =>
        //   navigate(`/Assessment/${item?.vqa_request_id}/Details`)
        // }
        >
          {item?.sponsor_name}
        </div>
      ),
    },
    {
      key: "vqa_status",
      header: "Status",
      sortable: false,
      width: "16%",
      render: (item) => <StatusIndicator status={item?.vqa_status} />,
    },
    {
      key: "capabilities",
      header: "Services",
      sortable: false,
      width: "20%",
      render: (item) => (
        <Capabilities
          services={item?.capabilities}
          icon={false}
          showLimited={false}
        />
      ),
    },
    {
      key: "vqa_auditor_name",
      header: "Auditor",
      sortable: false,
      width: "16%",
      render: (item) => <div>{item?.vqa_auditor_name}</div>,
    },
    {
      key: "vqa_created_on",
      header: "Request Date",
      sortable: false,
      width: "16%",
      render: (item) => dateFormat(item?.vqa_created_on),
    },
    {
      key: "released_on",
      header: "Released Date",
      sortable: false,
      width: "16%",
      render: (item) =>
        item?.released_on ? dateFormat(item?.released_on) : "--",
    },
    // {
    //   key: "actions",
    //   header: "Actions",
    //   sortable: false,
    //   width: "auto",
    //   // render: (item) => (
    //   //   <span
    //   //     onClick={() =>
    //   //       navigate(`/Assessment/${item?.vqa_request_id}/Details`)
    //   //     }
    //   //     className="font-semibold cursor-pointer text-black hover:underline"
    //   //   >
    //   //     View
    //   //   </span>
    //   // ),
    // },
  ];
  const [value, setValue] = useState({
    startDate: null,
    endDate: null,
  });
  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-8 w-full container">
      {/* <div className="flex justify-end mb-6 ">
        <div className="w-[300px]">
          <Datepicker
            value={value}
            onChange={(newValue) => setValue(newValue)}
            showShortcuts={true}
            displayFormat="MM/DD/YYYY"
          />
        </div>
      </div> */}
      <SummaryInfoCards
        totalAssessments={
          user === "Provider User"
            ? providerAssessmentMetrics?.total_vqa_requests
            : totalAssessmentData
        }
        totalProviders={providerTotalCountData}
        totalRFI={
          user === "Provider User"
            ? providerRfiMetricsData?.total_count
            : totalRfis?.total_count
        }
        totalSponsors={
          user === "Provider User"
            ? sponsorDistinctCount?.distinct_sponsor_count
            : sponsorTotalCountData?.total_count
        }
        navRfi={user === "Provider User" ? "/RFI/Requests" : "/Backend/Rfis"}
        navVQA={
          user === "Provider User"
            ? "/Provider/Assessment/History"
            : "/Backend/Assessment/Request/History"
        }
      />
      {user === "Provider User" && (
        <div className="p-4 rounded border mt-6 mb-6">
          <h3 className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            Diligent VQQ / VQA Request Summary
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            View your recent and historical VQQ and assessment request
            information.
          </p>
          <RFIVQAStatusTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            rfiCount={providerRfiMetricsData?.total_count}
            vqaCount={providerAssessmentMetrics?.total_vqa_requests}
          />{" "}
          {activeTab === "RFI" ? (
            // rfiIsLoading ? (
            //   <SkeletonLoader />
            // ) :
            <div className="mt-5 border border-gray-300 rounded-md">
              {/*h4 className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
          <a href="/RFI/history">Request for Information (RFI) Requests</a>
        </h4>*/}
              <div className="px-6 py-2">
                <p className="mt-1 text-2xl font-semibold">
                  {providerRfiMetricsData?.total_count}
                  <span className=" text-sm pl-2">VQQ Requests</span>
                </p>
                <div className="mt-5 bg-white rounded-md ">
                  <div className="rounded-md overflow-hidden">
                    <CategoryBar
                      values={[
                        providerRfiMetricsData?.sent_count,
                        providerRfiMetricsData?.in_progress_count,
                        providerRfiMetricsData?.review_count,
                        providerRfiMetricsData?.remediation_count_purchaser,
                        providerRfiMetricsData?.remediation_count_vendor,
                        providerRfiMetricsData?.complete_count,
                      ]}
                      colors={[
                        "blue", // Sent
                        "yellow", // In Progress
                        "purple", // In Review
                        "red", // In Remediation (Purchaser)
                        "orange", // In Remediation (Vendor)
                        "green", // Complete
                      ]}
                      showLabels={false}
                      className="!mt-0"
                    />
                  </div>
                </div>

                <ul className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-blue-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {providerRfiMetricsData?.sent_count}
                      </span>{" "}
                      Sent
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-yellow-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {providerRfiMetricsData?.in_progress_count}
                      </span>{" "}
                      In Progress
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-purple-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {providerRfiMetricsData?.review_count}
                      </span>{" "}
                      In Review
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-red-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {providerRfiMetricsData?.remediation_count_purchaser}
                      </span>{" "}
                      In Remediation (Purchaser)
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-orange-400"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {providerRfiMetricsData?.remediation_count_vendor}
                      </span>{" "}
                      In Remediation (Vendor)
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-green-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {providerRfiMetricsData?.complete_count}
                      </span>{" "}
                      Complete
                    </span>
                  </li>
                </ul>
                <Divider />
              </div>
              {/*
        <p className="mt-4 font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
          Recent Request Details
        </p> */}
              <CustomTable
                columns={RfiColumns}
                data={sortedItems || []}
                sortConfig={sortConfig}
                onSort={handleSort}
                emptyMessage="No VQQs found"
                emptyDescription="No VQQ requests are available at this time."
                // isLoading={rfiIsLoading}
              />{" "}
              {/* <div className="h-10 w-100% rounded flex items-center justify-end pr-4">
                <a
                  href="/RFI/Requests"
                  className="text-sm font-semibold py-2 text-blue-600 cursor-pointer hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                >
                  View more &#8594;
                </a>
              </div> */}
            </div>
          ) : (
            // vqaIsLoading ? (
            //   <SkeletonLoader />
            // )

            <div className="mt-5 border border-gray-300 rounded-md">
              <div className="px-6 py-2">
                <p className="mt-1 text-2xl font-semibold">
                  {providerAssessmentMetrics?.total_vqa_requests}
                  <span className=" text-sm pl-2">VQA Requests</span>
                </p>
                <div className="mt-5 rounded-md overflow-hidden bg-white">
                  <CategoryBar
                    values={[
                      providerAssessmentMetrics?.total_requested_vqa_requests,
                      providerAssessmentMetrics?.total_completed_vqa_requests,
                      providerAssessmentMetrics?.total_inprogress_vqa_requests,
                      providerAssessmentMetrics?.total_canceled_vqa_requests,
                    ]}
                    colors={["blue", "green", "yellow", "red"]}
                    showLabels={false}
                  />
                </div>
                <ul className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-blue-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {
                          providerAssessmentMetrics?.total_requested_vqa_requests
                        }
                      </span>{" "}
                      Requested
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-green-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {
                          providerAssessmentMetrics?.total_completed_vqa_requests
                        }
                      </span>{" "}
                      Completed
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-yellow-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {
                          providerAssessmentMetrics?.total_inprogress_vqa_requests
                        }
                      </span>{" "}
                      In Progress
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span
                      className="size-3 shrink-0 rounded-sm bg-red-500"
                      aria-hidden={true}
                    />
                    <span className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                      <span className="font-medium text-tremor-content-emphasis dark:text-dark-tremor-content-emphasis">
                        {providerAssessmentMetrics?.total_canceled_vqa_requests}
                      </span>{" "}
                      Cancelled
                    </span>
                  </li>
                </ul>
              </div>
              {/*
        <p className="mt-4 font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
          Request Details
        </p> */}
              <CustomTable
                columns={VQAColumns}
                data={sortedItems || []}
                sortConfig={sortConfig}
                onSort={handleSort}
                emptyMessage="No VQAs found"
                emptyDescription="No VQA requests are available at this time."
                // isLoading={vqaIsLoading}
              />{" "}
              {/* <div className="h-10 w-100% rounded flex items-center justify-end pr-4">
                <a
                  href="/Provider/Assessment/History"
                  className="text-sm font-semibold py-2 text-blue-600 cursor-pointer hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-600"
                >
                  View more &#8594;
                </a>
              </div> */}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
